// V2 Payment Method section.
//
// The payment method a customer switches to can be one of several kinds, and
// each kind needs a different set of fields. The user first picks a payment
// type (Bank PAD / Credit card / Cheque / Other) via a segmented control, and
// only the fields relevant to that type are shown — laid out compactly on a
// single horizontal row (placeholder-only, no labels), matching the mockup.
//
// Presented with the SectionPanel styling (blue header + outlined body) so it
// visually matches the "What changed?" board. Kept separate from the V1 form.

import React from "react";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { CustomTextField } from "../../../../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../../../../components/formsComponents/CustomDatePicker";
import { IAutomobilePolicyChangeProps } from "../../Steps/AutomobilePolicyChange.types";
import { SectionPanel } from "./SectionPanel";

// The payment types the change form supports. "Bank" is the PAD (pre-authorized
// debit) flow; the others map to the toggle in the mockup.
const PAYMENT_TYPE_OPTIONS = [
  { label: "Bank (PAD)", value: "Bank" },
  { label: "Credit card", value: "Credit Card" },
  { label: "Cheque", value: "Cheque" },
  { label: "Other", value: "Other" },
];

interface ICreditCardInformationV2Props extends IAutomobilePolicyChangeProps {
  // When true, render the fields without the blue SectionPanel header (used
  // inside the drawer, whose own header already shows the section title).
  hideHeading?: boolean;
}

// A segmented control built from plain Buttons — robust across themes and
// avoids ToggleButtonGroup layout quirks.
const SegmentedControl: React.FC<{
  options: { label: string; value: string }[];
  value: string | null | undefined;
  onSelect: (value: string) => void;
}> = ({ options, value, onSelect }) => (
  <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <Button
          key={option.value}
          type="button"
          disableElevation
          variant={selected ? "contained" : "outlined"}
          color={selected ? "primary" : "inherit"}
          size="small"
          onClick={() => onSelect(option.value)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2,
            py: 0.75,
            whiteSpace: "nowrap",
            ...(selected
              ? {}
              : { color: "text.secondary", borderColor: "divider" }),
          }}
        >
          {option.label}
        </Button>
      );
    })}
  </Stack>
);

export const CreditCardInformationV2: React.FC<
  ICreditCardInformationV2Props
> = ({ values, handleChange, setFieldValue, hideHeading = false }) => {
  // The loaded record may arrive with credit_card_info === null; fall back to
  // an empty object so field access below never throws (which would blank the
  // whole panel).
  const cc = values.credit_card_info || ({} as any);
  const paymentType = cc.payment_type;

  const handleCCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 16) {
      setFieldValue("credit_card_info.cc", value);
    }
  };

  // --- Bank (PAD) fields: single horizontal row, placeholder-only. ---
  const bankFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.bank_name"
          id="credit_card_info.bank_name"
          placeholder="Bank name (e.g. TD Canada Trust)"
          value={cc.bank_name || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.account_holder_name"
          id="credit_card_info.account_holder_name"
          placeholder="Account holder name"
          value={cc.account_holder_name || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.account_number"
          id="credit_card_info.account_number"
          placeholder="Account number"
          value={cc.account_number || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.institution_number"
          id="credit_card_info.institution_number"
          placeholder="Institution number (e.g. 004)"
          value={cc.institution_number || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.transit_number"
          id="credit_card_info.transit_number"
          placeholder="Transit number (e.g. 12345)"
          value={cc.transit_number || ""}
          onChange={handleChange}
        />
      </Grid>
    </Grid>
  );

  // --- Credit card fields: single horizontal row, placeholder-only. ---
  const creditCardFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <CustomTextField
          fullWidth
          type="number"
          name="credit_card_info.cc"
          id="credit_card_info.cc"
          placeholder="•••• •••• •••• ••••"
          value={cc.cc || ""}
          onChange={handleCCChange}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CustomDatePicker
          fullWidth
          inputFormat="MM/YYYY"
          views={["month", "year"]}
          value={cc.expiry || ""}
          onChange={(newValue) => {
            setFieldValue("credit_card_info.expiry", newValue);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CustomTextField
          type="number"
          fullWidth
          name="credit_card_info.cvv"
          id="credit_card_info.cvv"
          placeholder="•••"
          value={cc.cvv || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.account_holder_name"
          id="credit_card_info.account_holder_name"
          placeholder="Account holder name"
          value={cc.account_holder_name || ""}
          onChange={handleChange}
        />
      </Grid>
    </Grid>
  );

  // --- Cheque / Other fields: single horizontal row, placeholder-only. ---
  const genericFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.account_holder_name"
          id="credit_card_info.account_holder_name"
          placeholder="Account holder name"
          value={cc.account_holder_name || ""}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomTextField
          type="string"
          fullWidth
          name="credit_card_info.bank_name"
          id="credit_card_info.bank_name"
          placeholder="Bank name"
          value={cc.bank_name || ""}
          onChange={handleChange}
        />
      </Grid>
    </Grid>
  );

  const renderFieldsForType = () => {
    switch (paymentType) {
      case "Bank":
        return bankFields;
      case "Credit Card":
        return creditCardFields;
      case "Cheque":
      case "Other":
        return genericFields;
      default:
        return null;
    }
  };

  // NOTE: the drawer host (ChangeSectionDrawer) hides the FIRST `.MuiBox-root`
  // direct child of the section (to strip the shared V1 steps' blue LabelCard
  // bar). So this section deliberately avoids using `Box` as a top-level
  // child — `Stack` (MuiStack-root) is used for the section wrappers instead,
  // so nothing here gets accidentally hidden.
  const body = (
    <Stack spacing={2} sx={{ pr: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Applies from the next scheduled withdrawal. Carrier may require a signed
        PAD form.
      </Typography>

      {/* Payment type selector: Bank (PAD) / Credit card / Cheque / Other */}
      <SegmentedControl
        options={PAYMENT_TYPE_OPTIONS}
        value={paymentType}
        onSelect={(value) =>
          setFieldValue("credit_card_info.payment_type", value)
        }
      />

      {/* Type-specific fields (single horizontal row). */}
      {paymentType ? (
        renderFieldsForType()
      ) : (
        <Typography variant="body2" color="text.disabled">
          Select a payment type above to enter its details.
        </Typography>
      )}
    </Stack>
  );

  if (hideHeading) {
    return body;
  }

  return <SectionPanel heading="Payment Method">{body}</SectionPanel>;
};
